export default class BRecipeList
{
	static _observers = new WeakMap();
	static _isLoading = new WeakMap();
	static _abortControllers = new WeakMap();

	static initialize()
	{
		document.addEventListener("click", (event) => {
			const loadMoreBtn = event.target.closest("[data-list] .load-more");
			if (loadMoreBtn) {
				event.preventDefault();
				const list = loadMoreBtn.closest("[data-list]");
				if (list) {
					BRecipeList.loadMore(list);
				}
				return;
			}

			const sortTrigger = event.target.closest("[data-list-set-sort]");
			if (sortTrigger) {
				const list = sortTrigger.closest("[data-list]");
				if (list) {
					list.dataset.sort = sortTrigger.dataset.listSetSort;
					BRecipeList.reload(list);
				}
			}
		});

		document.querySelectorAll("[data-list]").forEach((list) => {
			BRecipeList.setupInfiniteScroll(list);
		});

		window.addEventListener("pageshow", (event) => {
			if (event.persisted) {
				document.querySelectorAll("[data-list]").forEach((list) => {
					BRecipeList._isLoading.set(list, false);
					BRecipeList.setupInfiniteScroll(list);
				});
			}
		});
	}

	static setupInfiniteScroll(list)
	{
		BRecipeList.teardownInfiniteScroll(list);

		const foot = list.querySelector(".b-recipe-list__foot");
		if (!foot || list.classList.contains("fully-loaded")) {
			return;
		}

		const btn = foot.querySelector(".load-more");
		const spinner = foot.querySelector(".b-recipe-list__spinner");
		if (btn) btn.classList.add("hidden");
		if (spinner) spinner.classList.remove("hidden");

		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting && !BRecipeList._isLoading.get(list) && !list.classList.contains("fully-loaded")) {
					BRecipeList.loadMore(list);
				}
			}
		}, {
			rootMargin: "400px",
		});

		observer.observe(foot);
		BRecipeList._observers.set(list, observer);
	}

	static teardownInfiniteScroll(list)
	{
		if (BRecipeList._observers.has(list)) {
			BRecipeList._observers.get(list).disconnect();
			BRecipeList._observers.delete(list);
		}
	}

	static getPage(list)
	{
		return parseInt(list.dataset.page) || 1;
	}

	static setPage(list, page)
	{
		list.dataset.page = page;
	}

	static getPerPage(list)
	{
		return parseInt(list.dataset.perPage) || 10;
	}

	static getSort(list)
	{
		return list.dataset.sort || "";
	}

	static getQuery(list)
	{
		return list.dataset.query || "";
	}

	static setQuery(list, query)
	{
		list.dataset.query = query;
	}

	static getUrl(list)
	{
		return list.dataset.url || "";
	}

	static getParams(list)
	{
		try {
			return JSON.parse(list.dataset.params || "{}");
		} catch (e) {
			return {};
		}
	}

	static serializeParams(obj, prefix = "")
	{
		const parts = [];

		if (Array.isArray(obj)) {
			for (const val of obj) {
				if (typeof val === "object" && val !== null) {
					parts.push(...BRecipeList.serializeParams(val, prefix + "[]"));
				} else {
					parts.push(encodeURIComponent(prefix + "[]") + "=" + encodeURIComponent(val));
				}
			}
		} else if (obj !== null && typeof obj === "object") {
			for (const key of Object.keys(obj)) {
				const newPrefix = prefix ? prefix + "[" + key + "]" : key;
				parts.push(...BRecipeList.serializeParams(obj[key], newPrefix));
			}
		} else if (obj !== null && obj !== undefined) {
			parts.push(encodeURIComponent(prefix) + "=" + encodeURIComponent(obj));
		}

		return parts;
	}

	static buildQueryString(params)
	{
		return BRecipeList.serializeParams(params).join("&");
	}

	static SKELETON_COUNT = 8;

	static createSkeletonHTML()
	{
		return `<div class="rcp-skeleton" data-skeleton>
			<div class="rcp-skeleton__inner">
				<div class="rcp-skeleton__img"></div>
				<div class="rcp-skeleton__text">
					<div class="rcp-skeleton__line rcp-skeleton__line--title"></div>
					<div class="rcp-skeleton__line rcp-skeleton__line--subtitle"></div>
				</div>
			</div>
		</div>`;
	}

	static insertSkeletons(list, count)
	{
		const container = list.querySelector(".b-recipe-list__grid");
		if (!container) {
			return;
		}

		const html = Array.from({ length: count }, () => BRecipeList.createSkeletonHTML()).join("");
		container.insertAdjacentHTML("beforeend", html);
	}

	static removeSkeletons(list)
	{
		const container = list.querySelector(".b-recipe-list__grid");
		if (!container) {
			return;
		}

		container.querySelectorAll("[data-skeleton]").forEach((el) => el.remove());
	}

	static setLoading(list, loading)
	{
		const loadMoreBtn = list.querySelector(".load-more");

		if (loadMoreBtn) {
			if (loading) {
				loadMoreBtn.setAttribute("disabled", "disabled");
			} else {
				loadMoreBtn.removeAttribute("disabled");
			}
		}
	}

	static loadMore(list)
	{
		if (BRecipeList._isLoading.get(list)) {
			return;
		}

		const nextPage = BRecipeList.getPage(list) + 1;
		BRecipeList._isLoading.set(list, true);
		BRecipeList.setLoading(list, true);
		BRecipeList.loadItems(list, nextPage, (response) => {
			BRecipeList.addPageSeparator(list, response.pagination.page);
			BRecipeList.addItems(list, response);
			BRecipeList.setPage(list, response.pagination.page);
		});
	}

	static reload(list)
	{
		BRecipeList.teardownInfiniteScroll(list);
		BRecipeList._isLoading.set(list, true);
		BRecipeList.setLoading(list, true);
		BRecipeList.removeAllItems(list);
		BRecipeList.insertSkeletons(list, BRecipeList.SKELETON_COUNT);
		BRecipeList.loadItems(list, 1, (response) => {
			list.classList.remove("fully-loaded");
			BRecipeList.removeSkeletons(list);
			BRecipeList.addItems(list, response);
			BRecipeList.setPage(list, 1);
		});
	}

	static loadItems(list, page, callback)
	{
		const params = BRecipeList.getParams(list);
		params.page = page;
		params.perPage = BRecipeList.getPerPage(list);
		params.sort = BRecipeList.getSort(list);
		params.query = BRecipeList.getQuery(list);
		// Ensure API returns recipe HTML and ads for list views (e.g. sort change)
		if (!params.templates) {
			params.templates = "grid.standard";
		}
		if (params.showAds === undefined || params.showAds === null) {
			params.showAds = 1;
		}

		const url = BRecipeList.getUrl(list) + "?" + BRecipeList.buildQueryString(params);

		if (BRecipeList._abortControllers.has(list)) {
			BRecipeList._abortControllers.get(list).abort();
		}
		const controller = new AbortController();
		BRecipeList._abortControllers.set(list, controller);

		fetch(url, {
			headers: { "Accept": "application/json" },
			signal: controller.signal,
		})
		.then((res) => res.json())
		.then((response) => {
			BRecipeList.setLoading(list, false);
			BRecipeList._isLoading.set(list, false);
			callback(response);

			const pagination = response.pagination || {};
			const foot = list.querySelector(".b-recipe-list__foot");
			const noResults = list.querySelector(".b-recipe-list__empty");

			if (pagination.page >= pagination.pages) {
				list.classList.add("fully-loaded");
				BRecipeList.teardownInfiniteScroll(list);
				if (foot) {
					foot.classList.add("hidden");
				}
			} else {
				if (foot) {
					foot.classList.remove("hidden");
				}
				BRecipeList.setupInfiniteScroll(list);
			}

			if (pagination.page === 1 && !pagination.total) {
				if (noResults) noResults.classList.remove("hidden");
				if (foot) foot.classList.add("hidden");
			} else {
				if (noResults) noResults.classList.add("hidden");
			}

			const numberEl = list.querySelector(".filtering .num .number");
			if (numberEl && pagination.total !== undefined) {
				numberEl.textContent = pagination.total;
			}

			const cancelBtn = list.querySelector(".filtering .cancel");
			if (cancelBtn) {
				if (response.richFilterCount > 0) {
					cancelBtn.classList.remove("hidden");
				} else {
					cancelBtn.classList.add("hidden");
				}
			}

			if (document.body.classList.contains("recipes--index") && typeof response.url === "string") {
				window.history.replaceState("", "", response.url);
			}

			window.dispatchEvent(new Event("ajaxFilter"));
		})
		.catch((err) => {
			if (err.name === "AbortError") {
				return;
			}
			BRecipeList.removeSkeletons(list);
			BRecipeList.setLoading(list, false);
			BRecipeList._isLoading.set(list, false);
		});
	}

	static removeAllItems(list)
	{
		const itemList = list.querySelector(".b-recipe-list__grid");
		if (itemList) {
			itemList.querySelectorAll(".b-recipe-list__item, [data-page-separator]").forEach((el) => el.remove());
		}
	}

	static addPageSeparator(list, page)
	{
		const container = list.querySelector(".b-recipe-list__grid");
		if (container) {
			const div = document.createElement("div");
			div.setAttribute("data-page-separator", page);
			container.appendChild(div);
		}
	}

	static addItems(list, response)
	{
		const container = list.querySelector(".b-recipe-list__grid");
		if (!container) {
			return;
		}
		const params = BRecipeList.getParams(list);
		const templateName = String(params.templates || "grid.standard").split(",")[0].trim();
		const data = Array.isArray(response.data) ? response.data : [];
		const recipes = [...data];
		const hasAdAt = (pos) => Array.isArray(response.ads) && response.ads.some((ad) => ad.position === pos);

		let position = 1;
		const ledger = {};

		while (recipes.length || ledger[position - 1] === "recipe" || hasAdAt(position)) {
			ledger[position] = false;

			if (response.ads) {
				for (const ad of response.ads) {
					if (ad.position === position) {
						container.insertAdjacentHTML("beforeend", ad.template);
						ledger[position] = "ad";
					}
				}
			}

			if (!ledger[position] && recipes.length) {
				const recipe = recipes.shift();
				if (recipe.templates && recipe.templates[templateName]) {
					container.insertAdjacentHTML("beforeend", recipe.templates[templateName]);
				}
				ledger[position] = "recipe";
			}

			position++;
			if (position > 500) break;
		}
	}
}
